import { useQuery } from '@tanstack/react-query'
import { configApi } from '../lib/api'
import { useCommitteeScope } from './useCommitteeScope'
import type { ClientConfig, ProductTier } from '../types/config'

// Bootstrap flags are effectively static for the session; fetch once and cache.
// The active committee scope folds into the key (not the request) so switching
// the parked acting committee forces a refetch -- the axios interceptor already
// appends committeeScope on every request.
export function useClientConfig() {
  const { activeCommitteeId } = useCommitteeScope()
  return useQuery<ClientConfig>({
    queryKey: ['config', activeCommitteeId ?? null],
    queryFn: () => configApi.get().then((r) => r.data),
    staleTime: Infinity,
    placeholderData: (prev) => prev,
  })
}

export function useDemoMode(): boolean {
  const { data } = useClientConfig()
  return data?.demoMode ?? false
}

// Default to 'Union' (the superset) when config hasn't loaded yet, so gated
// UI fails open toward showing more rather than hiding features prematurely.
export function useProductTier(): ProductTier {
  const { data } = useClientConfig()
  return data?.productTier ?? 'Union'
}

// Default to false when config hasn't loaded yet, so voter-file-match UI stays
// hidden until we positively know the backing data is present.
export function useVoterFilePresent(): boolean {
  const { data } = useClientConfig()
  return data?.voterFilePresent ?? false
}
